import { Theme } from 'contexts/Theme';
import styled from 'styled-components';
import colors from 'styles/colors';
import sizes from 'styles/sizes';
import { BrandCode } from 'types';
import { RoomViewPanelUiChanges } from './RefinedCruiseDetails.types';

export const CruiseDetailsOverlay = styled.div`
	position: fixed;
	inset: 0;
	height: 100%;
	width: 100%;
	transition: background-color 0.6s ease;
	pointer-events: ${({ isOpen, opening }: { opening: boolean; isOpen: boolean }) => (isOpen || opening ? 'initial' : 'none')};
	z-index: 10;

	${({ theme, opening, isOpen }: { theme: Theme; opening: boolean; isOpen: boolean }) =>
		({
			[BrandCode.RoyalCaribbean]: `
      background-color: ${opening || isOpen ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'};
    `,
			[BrandCode.Celebrity]: `
      background-color: ${opening || isOpen ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0)'};
    `,
		})[theme.brandCode]}
`;

export const CruiseDetailsModal = styled.div`
	height: 100%;
	width: 100%;
	z-index: 20;

	position: fixed;
	top: 0;
	background: white;
	transition: right 1.2s ease;

	right: ${({ opening, isOpen }: { opening: boolean; isOpen: boolean }) => (opening || isOpen ? '0' : '-100%')};

	pointer-events: initial;
	font-family: ${({ theme }) => theme.typography.fontFamily};

	@media (max-width: ${sizes.media.tabletLandscape}px) {
		max-width: 780px;
	}
	@media (max-width: ${sizes.media.tabletPortrait}px) {
		max-width: 768px;
	}

	${({ theme }: { theme: Theme }) =>
		({
			[BrandCode.RoyalCaribbean]: `
      max-width: 850px;
    `,
			[BrandCode.Celebrity]: `
      max-width: 1200px;
    `,
		})[theme.brandCode]}
`;

export const CruiseDetailsScrolling = styled.div`
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
`;

export const CloseSocialButtonsContainer = styled.div`
	display: flex;
	jusfitfy-content: space-between;
	padding: 10px 10px 10px 15px;
`;

export const CruiseDetailsCloseButton = styled.button`
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 50%;
	background-color: ${colors.primary.ice};
	padding: 10px;

	transition: background-color 0.2s;

	svg {
		height: 15px;
		width: 15px;
		path {
			fill: ${({ theme }: { theme: Theme }) => theme.palette.primary.main};
		}
	}

	@media (min-width: ${sizes.media.tabletMinWidth}px) {
		svg {
			height: 20px;
			width: 20px;
		}
	}

	&:hover {
		${({ theme }: { theme: Theme }) =>
			({
				[BrandCode.RoyalCaribbean]: `
        background-color: ${theme.palette.secondary.main};
      `,
				[BrandCode.Celebrity]: `
        background-color: ${theme.palette.primary.light};
      `,
			})[theme.brandCode]}
		svg {
			path {
				fill: ${({ theme }: { theme: Theme }) => theme.palette.common.white};
			}
		}
	}
`;

export const CruiseDetailsSocialsButtons = styled.div`
	display: flex;
	align-items: center;
	align-self: stretch;
	flex: 1 0 0;
	gap: 20px;

	& > div {
		padding: 0;
		align-items: center;
		svg {
			width: 22px;
			height: 22px;
		}
	}

	& > span {
		svg {
			width: 18px;
			height: 18px;
		}
	}

	@media (min-width: ${sizes.media.tabletMinWidth}px) {
		& > div {
			svg {
				width: 26px;
				height: 26px;
			}
		}

		& > span {
			svg {
				width: 22px;
				height: 22px;
			}
		}
	}
`;

export const CruiseDetailsHeaderContainer = styled.div`
	padding: 0px 20px;
	font-family: ${({ theme }) => theme.typography.fontFamily};
`;

export const CruiseDetailsDeparturePortContainer = styled.div`
	display: ${({ uiChanges }: { uiChanges: boolean }) => (uiChanges ? 'none' : 'block')};
	margin-top: 20px;
`;

export const CruiseDetailsPortsListContainer = styled.div`
	display: ${({ uiChanges }: { uiChanges: boolean }) => (uiChanges ? 'none' : 'block')};

	& > div {
		display: block;
		margin-top: 10px;
	}
`;

export const UrgencyMessageContainer = styled.div`
	@media (min-width: ${sizes.media.tabletMinWidth}px) {
		padding: 0;
	}

	@media (max-width: ${sizes.media.tabletMinWidth - 1}px) {
		& > div {
			height: auto;
			margin-top: 10px;
		}
	}
`;

export const CruiseDetailsItineraryButtonContainer = styled.div<RoomViewPanelUiChanges>`
	display: flex;
	flex-direction: column;
	align-items: start;
	margin-top: 20px;

	@media (min-width: ${sizes.media.tabletMinWidth}px) {
		flex-direction: row;
		align-items: center;
		gap: 20px;
	}

	& button {
		font-family: ${({ theme }) => theme.typography.fontFamilySemiBold};
		${({ theme, uiChanges }) =>
			({
				[BrandCode.RoyalCaribbean]: `
        padding: 11.5px 50px;
        font-size: 14px;
				width: fit-content;
				${
					uiChanges
						? `
						line-height: 16px;
						font-family: ${theme.typography.fontFamily};
						@media (min-width: ${sizes.media.tabletMinWidth}px) {
							padding: 12px 32px;
							font-size: 18px;
							line-height: 32px;
							height: 56px;
						}
					`
						: `
						@media (min-width: ${sizes.media.tabletMinWidth}px) {
							padding: 13px 50px;
							font-size: 16px;
						}
					`
				}
      `,
				[BrandCode.Celebrity]: `
        text-transform: uppercase;
        padding: 5px 16px;
        font-size: 13px;
				${uiChanges && 'width: 184px'};
      `,
			})[theme.brandCode]}
	}
`;
